import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import '../styles/MainPage.css';
import { FaChevronLeft, FaChevronRight, FaSearch } from 'react-icons/fa';
import AuthModal from './AuthModal';

const bannerImages = [
  "/assets/banners/promop1.webp",
  "/assets/banners/promop2.webp",
  "/assets/banners/banner3.webp"
];

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();

// Сопоставление названия игры и файла картинки
const gameImageMap = {
  "Crazy Monkey": "crazymonkey.png",
  "Crazy Monkey 2": "crazymonkey2.png",
  "Fruit Cocktail": "fruitcoctail.png",
  "Fruit Cocktail 2": "fruitcoctail2.png",
  "Golden Antelope": "goldenantilope.png",
  "Garage": "garage.png",
  "Gnome": "gnome.png",
  "The Heat": "theheat.png",
  "Island": "island.png",
  "Island 2": "island2.png",
  "Keks": "keks.png",
  "Lucky Haunter": "luckyhunter.png",
  "Pumpkin Fairy": "pumpkinfairy.png",
  "Pirate": "pirate.png",
  "Pirate 2": "pirate2.png",
  "Rock Climber": "rockclimber.png",
  "Resident": "resident.png",
  "Sweet Life": "sweetlife.png",
  "Sweet Life 2": "sweetlife2.png",
  "Tower Poker": "towerpocker.png",
  "Win Poker": "winpocker.png"
};

const MainPage = () => {
  const { isAuth } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [fade, setFade] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const nextBanner = () => {
    setFade(true);
    setTimeout(() => {
      setCurrentBanner((prev) => (prev + 1) % bannerImages.length);
      setFade(false);
    }, 300);
  };

  const prevBanner = () => {
    setFade(true);
    setTimeout(() => {
      setCurrentBanner((prev) => (prev - 1 + bannerImages.length) % bannerImages.length);
      setFade(false);
    }, 300);
  };

  useEffect(() => {
    fetchGames();
    const interval = setInterval(nextBanner, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const fetchGames = async () => {
    try {
      const response = await fetch(`${API_URL}/api/games`, {

      });

      if (!response.ok) {
        throw new Error('Ошибка загрузки игр');
      }

      const data = await response.json();
      setGames(data);
    } catch (err) {
      console.error('Ошибка при загрузке игр:', err);
      setError('Не удалось загрузить список игр');
    } finally {
      setLoading(false);
    }
  };

  const filteredGames = games.filter(game =>
    game.public.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleBonusBannerClick = () => {
    if (isAuth) {
      // Авторизованный пользователь - перенаправляем на FreeMoney
      navigate('/freemoney');
    } else {
      // Неавторизованный пользователь - показываем модальное окно регистрации
      setShowAuthModal(true);
    }
  };

  const handleCashbackBannerClick = () => {
    if (isAuth) {
      // Авторизованный пользователь - перенаправляем на FreeMoney к баннеру кэшбэк
      navigate('/freemoney');

      // Небольшая задержка для загрузки страницы, затем прокручиваем к баннеру кэшбэк
      setTimeout(() => {
        const cashbackElement = document.getElementById('banner-cashback');
        if (cashbackElement) {
          cashbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          cashbackElement.click(); // Открываем модальное окно
        }
      }, 500);
    } else {
      // Неавторизованный пользователь - показываем модальное окно регистрации
      setShowAuthModal(true);
    }
  };

  const handleRakebackBannerClick = () => {
    if (isAuth) {
      // Авторизованный пользователь - перенаправляем на FreeMoney к баннеру Rakeback
      navigate('/freemoney');

      // Небольшая задержка для загрузки страницы, затем прокручиваем к баннеру rakeback
      setTimeout(() => {
        const rakebackElement = document.getElementById('banner-rakeback');
        if (rakebackElement) {
          rakebackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          rakebackElement.click(); // Открываем модальное окно
        }
      }, 500);
    } else {
      // Неавторизованный пользователь - показываем модальное окно регистрации
      setShowAuthModal(true);
    }
  };

  const handleReferralBannerClick = () => {
    if (isAuth) {
      // Авторизованный пользователь - перенаправляем на страницу Referral Program
      navigate('/referral');
    } else {
      // Неавторизованный пользователь - показываем модальное окно регистрации
      setShowAuthModal(true);
    }
  };

  if (loading) {
    return <div className="loading">{t('Main.loading')}</div>;
  }

  if (error) {
    return <div className="error">{t('Main.error')}</div>;
  }

  return (
    <div className="mainpage">
      <div className="banner-row">
        <div className="banner-horizontal-group">
          <button
            onClick={handleBonusBannerClick}
            className="banner-button"
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderRadius: '18px',
              overflow: 'hidden'
            }}
          >
            <img src={`/assets/banners/promop1.webp?v=${Date.now()}`} alt="Бонус +500%" className="banner-horizontal" />
          </button>
          <button
            onClick={handleCashbackBannerClick}
            className="banner-button"
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderRadius: '18px',
              overflow: 'hidden'
            }}
          >
            <img src={`/assets/banners/promop2.webp?v=${Date.now()}`} alt="Кэшбек и бонусы" className="banner-horizontal" />
          </button>
        </div>
        <button
          onClick={handleRakebackBannerClick}
          className="banner-button"
          style={{
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderRadius: '18px',
            overflow: 'hidden'
          }}
        >
          <img src="/assets/banners/rakeback.webp" alt="Rakeback" className="banner-vertical" />
        </button>
      </div>

      <div className="search-bar">
        <div className="search-input-wrapper">
          <FaSearch className="search-icon" />
          <span className="search-label">Поиск</span>
          <input
            ref={searchInputRef}
            type="text"
            placeholder=""
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>

      <div className="content">
        {!loading && (
          <div className="games-grid">
            {filteredGames.map((game) => (
              <Link
                key={game.id}
                to={`/game/${game.id}${!isAuth ? '?demo=true' : ''}${isAuth ? '?' : '&'}name=${encodeURIComponent(game.public)}`}
                className="game-card"
              >
                <div className="game-image-container">
                  <img
                    src={`/assets/Igrosoft/${gameImageMap[game.public] || 'game-placeholder.png'}`}
                    alt={game.name}
                    className="game-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/game-placeholder.png';
                    }}
                  />
                </div>
                <h3>{game.public}</h3>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Модальное окно авторизации */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          isLoginType={false}
        />
      )}
    </div>
  );
};

export default MainPage;